import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { form, required } from '@angular/forms/signals';
import {
  FulfillmentType,
  FulfillmentTypeLabels,
  FulfillmentWindowSettingsV1,
  UpdateFulfillmentWindowSettingsRequestV1
} from '@producers/fulfillment-window-settings/fulfillment-window-settings';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { firstValueFrom } from 'rxjs';

import { FulfillmentWindowSettingsService } from '@core/services/fulfillment-window-settings';

interface SettingsFormModel {
  offsetMinutes: number;
  durationMinutes: number;
}

@Component({
  selector: 'app-fulfillment-window-settings-table',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    TagModule,
    TooltipModule,
    DialogModule,
    IconField,
    InputIcon,
    InputTextModule,
    InputNumberModule,
    ProgressSpinnerModule
  ],
  templateUrl: './fulfillment-window-settings-table.html',
  styleUrl: './fulfillment-window-settings-table.scss'
})
export class FulfillmentWindowSettingsTable {
  private readonly settingsService = inject(FulfillmentWindowSettingsService);
  private readonly messageService = inject(MessageService);

  readonly settingsResource = this.settingsService.createSettingsResource();

  readonly searchTerm = signal<string>('');
  readonly showDialog = signal<boolean>(false);
  readonly selectedFulfillmentType = signal<FulfillmentType | null>(null);
  readonly saving = signal<boolean>(false);

  readonly settingsFormModel = signal<SettingsFormModel>(this.emptyForm());

  readonly settingsForm = form(this.settingsFormModel, (schemaPath) => {
    required(schemaPath.offsetMinutes, { message: 'Offset minutes is required' });
    required(schemaPath.durationMinutes, { message: 'Duration minutes is required' });
  });

  readonly columns = signal([
    { field: 'fulfillmentType', header: 'Fulfillment Type', sortable: true },
    { field: 'offsetMinutes', header: 'Offset (minutes)', sortable: true },
    { field: 'durationMinutes', header: 'Duration (minutes)', sortable: true },
    { field: 'actions', header: 'Actions', sortable: false }
  ]);

  readonly filteredSettings = computed<FulfillmentWindowSettingsV1[]>(() => {
    const searchTerm = this.searchTerm().toLowerCase();
    const settings: FulfillmentWindowSettingsV1[] = this.settingsResource.value() ?? [];
    if (!searchTerm) return settings;

    return settings.filter((s) => this.getFulfillmentTypeLabel(s.fulfillmentType).toLowerCase().includes(searchTerm));
  });

  readonly isLoading = computed(() => this.settingsResource.isLoading());

  readonly dialogTitle = computed(() => {
    const type = this.selectedFulfillmentType();
    return type ? `Edit ${this.getFulfillmentTypeLabel(type)}` : 'Edit Settings';
  });

  onSearch(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.searchTerm.set(value);
  }

  onEditSettings(setting: FulfillmentWindowSettingsV1): void {
    this.selectedFulfillmentType.set(setting.fulfillmentType);
    this.settingsFormModel.set({
      offsetMinutes: setting.offsetMinutes,
      durationMinutes: setting.durationMinutes
    });
    this.showDialog.set(true);
  }

  async onSaveSettings(): Promise<void> {
    if (!this.isFormValid()) {
      this.showError('Please fix validation errors');
      return;
    }

    const fulfillmentType = this.selectedFulfillmentType();
    if (!fulfillmentType) return;

    this.saving.set(true);
    const formData = this.settingsFormModel();
    const payload: UpdateFulfillmentWindowSettingsRequestV1 = {
      offsetMinutes: formData.offsetMinutes,
      durationMinutes: formData.durationMinutes
    };

    try {
      await firstValueFrom(this.settingsService.updateSettings(fulfillmentType, payload));
      this.showSuccess('Fulfillment window settings updated successfully');
      this.showDialog.set(false);
      this.settingsResource.reload();
    } catch (error) {
      this.showError('Failed to update settings');
      console.error('Save settings error:', error);
    } finally {
      this.saving.set(false);
    }
  }

  getFulfillmentTypeLabel(type: FulfillmentType): string {
    return FulfillmentTypeLabels[type] ?? type;
  }

  isFormValid(): boolean {
    const model = this.settingsFormModel();
    return model.offsetMinutes >= 0 && model.durationMinutes > 0;
  }

  private emptyForm(): SettingsFormModel {
    return {
      offsetMinutes: 0,
      durationMinutes: 0
    };
  }

  private showError(message: string): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: message
    });
  }

  private showSuccess(message: string): void {
    this.messageService.add({
      severity: 'success',
      summary: 'Success',
      detail: message
    });
  }
}
